import { useMutation, useQueryClient } from "react-query";
import { API_PATH, QUERY_KEYS } from "../utils/config";
import API from "./request";
import { postLogin } from "./postLogin";

export const useLogin = () => {
  // Bounded wait: a login request that never settles used to leave the visitor
  // staring at a disabled button and a spinner forever, with no message and no
  // way to retry. See src/services/loginTimeout.js for the measurement.
  return useMutation((data) => postLogin(API, data));
};

export const useLogout = () => {
  return useMutation((data) => API.post(API_PATH.LOGOUT, data));
};

export const useSignup = () => {
  return useMutation((data) => API.post(API_PATH.SIGN_UP, data));
};

// Record acceptance of one legal document at its CURRENT server version. Body:
// { doc_type }. The version is stamped server-side (never client-supplied).
export const useRecordTermsAcceptance = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (doc_type) =>
      API.post(API_PATH.RECORD_TERMS_ACCEPTANCE, { doc_type }),
    {
      onSuccess: () =>
        queryClient.invalidateQueries([QUERY_KEYS.termsAcceptanceStatus]),
    }
  );
};

// Explicitly leave the Earnings/Referral program. Clears ONLY the referral/FA
// document requirement (base Terms/Privacy are never affected).
export const useLeaveEarningsProgram = () => {
  const queryClient = useQueryClient();
  return useMutation(() => API.post(API_PATH.LEAVE_EARNINGS_PROGRAM), {
    onSuccess: () =>
      queryClient.invalidateQueries([QUERY_KEYS.termsAcceptanceStatus]),
  });
};

export const useVerifyEmailLink = () => {
  return useMutation((data) => API.post(API_PATH.VERIFY_EMAIL_LINK, data));
};

export const useResendVerificationLink = () => {
  return useMutation((data) =>
    API.post(API_PATH.RESEND_VERIFICATION_LINK, data)
  );
};

export const useDiscountPromoCode = () => {
  return useMutation((data) => API.post(API_PATH.DISCOUNT_CODE, data));
};

export const useVerifyEmailOtp = () => {
  return useMutation((data) => API.post(API_PATH.VERIFY_EMAIL_OTP, data));
};

export const useVerifyPhoneOtp = () => {
  return useMutation((data) => API.post(API_PATH.VERIFY_PHONE_OTP, data));
};

export const useSendEmailOtp = () => {
  return useMutation((data) => API.post(API_PATH.SEND_EMAIL_OTP, data));
};

export const useSendPhoneOtp = () => {
  return useMutation((data) => API.post(API_PATH.SEND_PHONE_OTP, data));
};

export const useForgotVerifyEmailOtp = () => {
  return useMutation((data) =>
    API.post(API_PATH.FORGOT_VERIFY_EMAIL_OTP, data)
  );
};

export const useForgotVerifyPhoneOtp = () => {
  return useMutation((data) =>
    API.post(API_PATH.FORGOT_VERIFY_PHONE_OTP, data)
  );
};

export const useForgotSendEmailOtp = () => {
  return useMutation((data) => API.post(API_PATH.FORGOT_SEND_EMAIL_OTP, data));
};

export const useForgotSendPhoneOtp = () => {
  return useMutation((data) => API.post(API_PATH.FORGOT_SEND_PHONE_OTP, data));
};

// Login L4 -- passwordless email magic-link.
export const useSendMagicLink = () => {
  return useMutation((data) => API.post(API_PATH.MAGIC_LINK_SEND, data));
};

export const useRedeemMagicLink = () => {
  return useMutation((data) => API.post(API_PATH.MAGIC_LINK_REDEEM, data));
};

export const useVerifyPassword = () => {
  return useMutation((data) => API.post(API_PATH.VERIFY_PASSWORD, data));
};

export const useRemovePhone2fa = () => {
  return useMutation((data) => API.post(API_PATH.REMOVE_PHONE_2FA, data));
};

// Permanent, destructive account deletion. Sent as an HTTP DELETE with a JSON
// body { password?, reason } — axios needs the body under `data` for DELETE.
// The backend (DeleteAccountView) requires `reason`, requires `password` only
// when the account has a usable password, logs the deletion, tears down the
// subscription + sessions/tokens, then hard-deletes the user (204 on success).
export const useDeleteAccount = () => {
  return useMutation((data) =>
    API.delete(API_PATH.DELETE_ACCOUNT, { data })
  );
};

export const useResetPassword = () => {
  return useMutation((data) => API.post(API_PATH.RESET_PASSWORD, data));
};

export const useSubmitFeedback = () => {
  return useMutation((data) => API.post(API_PATH.SUBMIT_FEEDBACK, data));
};

export const useCreateWheel = () => {
  return useMutation((data) => API.post(API_PATH.CREATE_WHEEL, data));
};

export const useUpdateWheel = (wheelID) => {
  return useMutation((data) => API.put(API_PATH.EDIT_WHEEL(wheelID), data));
};

export const useUpdateWheelAfterCreate = () => {
  return useMutation((data) => API.put(API_PATH.EDIT_WHEEL(data.id), data));
};

export const useDeleteWheel = () => {
  return useMutation((data) =>
    API.delete(API_PATH.DELETE_WHEEL(data.wheelID), data)
  );
};

export const useUpdatePrimaryWheel = (wheelID) => {
  return useMutation((wheelID) => {
    return API.post(API_PATH.UPDATE_PRIMARY_WHEEL(wheelID), {});
  });
};

export const useUpdateWheelVisibility = (wheelID) => {
  return useMutation((wheelID) => {
    return API.post(API_PATH.UPDATE_WHEEL_VISIBILITY(wheelID), {});
  });
};

export const useUpdateWheelVisibilityV2 = () => {
  return useMutation((data) => {
    return API.post(API_PATH.UPDATE_WHEEL_VISIBILITY_V2(data?.wheelId), data);
  });
};

// Roadmap #7 — owner opt-in toggle to make a PUBLIC wheel embeddable on
// third-party sites. Body: { enabled: bool }. Owner-only + validated server-side.
export const useSetWheelEmbedding = () => {
  return useMutation((data) => {
    return API.post(API_PATH.SET_WHEEL_EMBEDDING(data?.wheelId), {
      enabled: data?.enabled,
    });
  });
};

export const useRemovePrimaryWheel = () => {
  return useMutation(() => {
    return API.post(API_PATH.REMOVE_PRIMARY_WHEEL);
  });
};

export const useClonedWheel = () => {
  return useMutation((data) => {
    return API.post(API_PATH.CLONE_WHEEL(data?.wheelId), data);
  });
};

export const useCloneWheel = () => {
  return useMutation((data) => {
    return API.post(API_PATH.CLONE_WHEEL(data?.wheelId), data);
  });
};

export const useUploadWheelImage = () => {
  return useMutation((data) =>
    API.put(
      API_PATH.UPLOAD_WHEEL_IMAGE(data.wheelId),
      { wheel_image: data.wheel_image },
      {
        headers: { "Content-Type": "multipart/form-data" },
      }
    )
  );
};

export const useSaveExternalImage = () => {
  return useMutation((data) => API.post(API_PATH.SAVE_EXTERNAL_IMAGE(), data));
};

export const useUserUpdateProfile = () => {
  return useMutation((data) => API.patch(API_PATH.USER_UPDATE_PROFILE, data));
};

export const useCreateBusinessBoxV2 = () => {
  return useMutation((data) => API.post(API_PATH.CREATE_BUSINESSBOXV2, data));
};

export const useGetBusinessBoxV2 = (userId) => {
  return useMutation((data) =>
    API.get(API_PATH.GET_BUSINESSBOXV2(data.userid), data)
  );
};

export const useGoogleToken = () => {
  return useMutation((data) => API.post(API_PATH.GOOGLE_LOGIN, data));
};

export const useFacebook = () => {
  return useMutation((data) => API.post(API_PATH.FACEBOOK_LOGIN, data));
};

export const useAppleToken = () => {
  return useMutation((data) => API.post(API_PATH.APPLE_LOGIN, data));
};

export const usePurchaseSubscription = () => {
  return useMutation((data) => API.post(API_PATH.PURCHASE_SUBSCRIPTION, data));
};

export const useChangeSubscription = () => {
  return useMutation((data) => API.post(API_PATH.CHANGE_SUBSCRIPTION, data));
};

export const useCancelSubscription = () => {
  return useMutation((data) => API.post(API_PATH.CANCEL_SUBSCRIPTION, data));
};

export const useCancelScheduledSubscriptionChange = () => {
  return useMutation(() =>
    API.post(API_PATH.CANCEL_SCHEDULED_SUBSCRIPTION_CHANGE)
  );
};

export const useRemoveCard = () => {
  return useMutation((data) => API.post(API_PATH.DELETE_CARD, data));
};

export const useAddCard = () => {
  return useMutation((data) => API.post(API_PATH.ADD_CARD, data));
};

export const useSetDefaultCard = () => {
  return useMutation((data) => API.post(API_PATH.SET_DEFAULT_CARD, data));
};

export const useChangePassword = () => {
  return useMutation((data) => API.put(API_PATH.CHANGE_PASSWORD, data));
};

export const useFeedback = () => {
  return useMutation((data) =>
    API.post(API_PATH.SEND_FEEDBACK(data.user_id), data)
  );
};

export const useCreateBusinessBox = () => {
  return useMutation((data) => API.post(API_PATH.CREATE_BUSINESSBOX, data));
};

export const useCreateBusinessBoxProfile = () => {
  return useMutation((data) =>
    API.post(API_PATH.UPDATE_BUSINESSBOX_PROFILE(data.id), data)
  );
};

export const useGetBusinessBox = () => {
  return useMutation((data) =>
    API.get(API_PATH.GET_BUSINESSBOX(data.userid), data)
  );
};

export const useUpdatBusinessBox = () => {
  return useMutation((data) =>
    API.put(API_PATH.UPDATE_BUSINESSBOX(data.id), data)
  );
};

export const useUpdateBusinessBoxV2 = () => {
  return useMutation((data) =>
    API.put(API_PATH.UPDATE_BUSINESSBOX_V2(data.id), data)
  );
};

export const useUpdatePrimaryBusinessBox = () => {
  return useMutation((data) =>
    API.post(API_PATH.UPDATE_PRIMARY_BUSINESSBOX(data.id), data)
  );
};

export const useDeleteBusinessBox = () => {
  return useMutation((data) =>
    API.delete(API_PATH.UPDATE_BUSINESSBOX(data.id), data)
  );
};

export const useDuplicateWheel = () => {
  return useMutation(
    (data) => API.post(API_PATH.DUPLICATE_WHEEL(data), data.data),
    {
      onSuccess: () => {
        // Refresh the page on successful mutation
        window.location.reload();
      },
    }
  );
};

export const useCloneSegment = () => {
  return useMutation((data) =>
    API.post(API_PATH.CLONE_SEGMENT(data.ActivityID), data.data)
  );
};

export const useGetPrivateWheelList = () => {
  return useMutation((data) =>
    API.get(
      API_PATH.GET_USER_PRIVATE_WHEELS_BY_ID(
        data.user_id,
        data.page,
        data.content
      ),
      data.data
    )
  );
};

export const usePostMediaBrowserData = () => {
  return useMutation((data) =>
    API.get(
      API_PATH.GET_MEDIA_BROWSER_DATA(
        data.text,
        data.type,
        data.source,
        data.page,
        data.per_page
      ),
      data.data
    )
  );
};

export const usePostMediaBrowserDataV2 = () => {
  return useMutation((data) =>
    API.get(
      API_PATH.GET_MEDIA_BROWSER_DATA_v2(
        data.text,
        data.type,
        data.source,
        data.page,
        data.per_page,
        data.offset, // Include offset if available
        data.limit // Include limit if available
      ),
      data.data
    )
  );
};

export const useGetUnlockWheelList = () => {
  return useMutation((data) =>
    API.get(
      API_PATH.GET_USER_UNLOCKED_WHEELS_BY_ID(
        data.user_id,
        data.page,
        data.content
      ),
      data.data
    )
  );
};

export const useGetClonedWheelList = () => {
  return useMutation((data) =>
    API.get(
      API_PATH.GET_USER_CLONED_WHEELS_BY_ID(
        data.user_id,
        data.page,
        data.content
      ),
      data.data
    )
  );
};

export const useGetClonedSegmentsList = () => {
  return useMutation((data) =>
    API.get(
      API_PATH.GET_USER_CLONED_SEGMENTS_BY_ID(data.page, data.content),
      data.data
    )
  );
};

export const useAutoGenerateWheel = () => {
  return useMutation((data) => API.post(API_PATH.AUTO_GENERATE_WHEEL, data));
};

export const useGenerateAiWheel = () => {
  return useMutation((data) => API.post(API_PATH.GENERATE_AI_WHEEL, data));
};

export const useReferralsInvite = () => {
  return useMutation((data) => API.post(API_PATH.REFERRAL_INVITES, data));
};

export const usePaymentWithdraw = () => {
  return useMutation((data) => API.post(API_PATH.PAYMENT_WITHDRAW, data));
};

export const useConnectStripeAccount = () => {
  return useMutation(() => API.post(API_PATH.CONNECT_STRIPE, {}));
};

const unlockWheel = async ({ wheelId, accessCode }) => {
  const body = {
    lock_type: 2,
    access_code: accessCode,
  };
  return API.post(API_PATH.UNLOCK_WHEEL(wheelId), body);
};

export const useUnlockWheel = () => {
  return useMutation(unlockWheel);
};

export const useCreateWheelV2 = () => {
  return useMutation((data) => API.post(API_PATH.CREATE_WHEEL_V2, data));
};

export const useUpdateWheelV2 = (id) => {
  return useMutation((data) => API.put(API_PATH.UPDATE_WHEEL_V2(id), data));
  // if (id)
};

export const useDeleteWheelV2 = () => {
  return useMutation((id) => API.delete(API_PATH.DELETE_WHEEL_V2(id)));
};

export const useFavoriteWheel = () => {
  return useMutation((id) => API.post(API_PATH.FAVORITE_WHEEL(id)));
};

export const useUnlockWheelV2 = () => {
  return useMutation(({ wheelId, ...rest }) =>
    API.post(API_PATH.UNLOCK_WHEEL_V2(wheelId), { ...rest })
  );
};

// SEGMENT LOCK §2 — per-segment unlock. Body {password} for a password lock; email
// locks 403 (no self-unlock), paid locks 402 + price. BE 404s while SEGMENT_LOCK_ENABLED
// is OFF, so the flag-OFF viewer never reaches a live endpoint.
export const useUnlockSegment = () => {
  return useMutation(({ activityId, ...rest }) =>
    API.post(API_PATH.UNLOCK_SEGMENT(activityId), { ...rest })
  );
};

export const useGetSuggestedColorPalette = (options = {}) => {
  return useMutation({
    mutationFn: (payload) =>
      API.post(API_PATH.GET_SUGGESTED_COLOR_PALETTE, payload),
    onSuccess: (data) => {
      if (options?.onSuccess) {
        options.onSuccess(data);
      }
    },
    onError: (err) => {},
  });
};
export const useCreateFolder = () => {
  return useMutation((data) => API.post(API_PATH.CREATE_FOLDER, data));
};

export const useStoreEmailReferralCode = () => {
  return useMutation((data) =>
    API.post(API_PATH.STORE_EMAIL_REFERRAL_CODE, data)
  );
};

// Mechanism-agnostic deferred email-carry for the "download the app" funnel:
// remembers EVERY guest's email (referral or organic), keyed by an opaque token
// the BE mints, so the freshly-installed native app can retrieve it after an
// install the app stores pass no custom data to. Unlike STORE_EMAIL_REFERRAL_CODE,
// referral_code is OPTIONAL here.
export const useStorePendingAppInstall = () => {
  return useMutation((data) =>
    API.post(API_PATH.STORE_PENDING_APP_INSTALL, data)
  );
};
export const useUpdateFolder = () => {
  return useMutation((data) => {
    const { id, ...restData } = data;
    return API.patch(API_PATH.UPDATE_FOLDER(id), restData);
  });
};

export const useDeleteFolder = () => {
  return useMutation((id) => API.delete(API_PATH.DELETE_FOLDER(id)));
};

export const useMoveFolder = () => {
  return useMutation((data) => {
    const { destinationId, wheel_id, folder_to_move } = data;
    // Folder -> root: the dedicated move-to-root endpoint (GALLERY_FOLDERS_V2,
    // backend PR #1188) sets parent=None, which the plain /move/ endpoint cannot.
    if (folder_to_move && destinationId == null) {
      return API.put(API_PATH.MOVE_FOLDER_TO_ROOT(folder_to_move), {});
    }
    const payload = wheel_id ? { wheel_id } : { folder_to_move };
    return API.put(API_PATH.MOVE_ITEM(destinationId), payload);
  });
};

export const useRemoveWheelFromFolder = () => {
  return useMutation(({ folder_id, ...rest }) =>
    API.put(API_PATH.REMOVE_WHEEL(folder_id), rest)
  );
};

// Persist a Collections drag-to-reorder (see API_PATH.REORDER_FOLDERS — the
// backend route this calls does not exist yet). `parent` is the folder id the
// reordered siblings live under (null = root level); `ordered_ids` is the full
// new sibling order.
export const useReorderFolders = () => {
  return useMutation(({ parent, ordered_ids }) =>
    API.patch(API_PATH.REORDER_FOLDERS, { parent, ordered_ids })
  );
};

export const useBroadcastChanges = () => {
  return useMutation((wheelId) => API.put(API_PATH.BROADCAST_CHANGES(wheelId)));
};
export const useSubmitReport = () => {
  return useMutation((data) => API.post(API_PATH.SUBMIT_REPORT, data));
};

export const useAddEmails = () => {
  const queryClient = useQueryClient();

  return useMutation(
    async ({ wheelId, emails }) => {
      const response = await API.post(`${API_PATH.ADD_EMAILS(wheelId)}`, {
        emails,
      });
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries("emailPermissions");
      },
      onError: (error) => {
        console.error("Failed to add emails:", error);
      },
    }
  );
};

export const useDeleteWheelEmailPermission = () => {
  const queryClient = useQueryClient();

  return useMutation(
    async ({ wheelId, emails }) => {
      const response = await API.post(
        API_PATH.DELETE_WHEEL_EMAIL_PERMISSION(wheelId),
        { emails }
      );
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries("emailPermissions");
      },
      onError: (error) => {
        console.error("Failed to delete email permission:", error);
      },
    }
  );
};

export const useSubmitDMCATakedown = () => {
  return useMutation((data) => API.post(API_PATH.DMCA_TAKEDOWN, data));
};

export const useSubmitDMCACounter = () => {
  return useMutation((data) => API.post(API_PATH.DMCA_COUNTER, data));
};

export const useSubmitContentRemovalDispute = () => {
  return useMutation((data) =>
    API.post(API_PATH.CONTENT_REMOVAL_DISPUTE, data)
  );
};

export const useToggleWheelActive = () => {
  return useMutation(
    (wheelId) => API.post(API_PATH.TOGGLE_WHEEL_ACTIVE(wheelId)),
    {
      onError: (error) => {
        console.error("Failed to toggle wheel active status:", error);
      },
    }
  );
};

export const useValidatePromoCode = (options = {}) => {
  return useMutation((data) => API.post(API_PATH.VALIDATE_PROMO_CODE, data), {
    onSuccess: (data) => {
      if (options?.onSuccess) {
        options.onSuccess(data);
      }
    },
    onError: (error) => {
      if (options?.onError) {
        options.onError(error);
      }
    },
  });
};

/**
 * Submit the Help-page contact/support form (Roadmap #14). Routes to
 * support@iyuga.net via the backend. Payload: { name, email, subject, message,
 * recaptcha_token }. See API_PATH.SUBMIT_SUPPORT_REQUEST.
 */
export const useSubmitSupportRequest = (options = {}) => {
  return useMutation(
    (data) => API.post(API_PATH.SUBMIT_SUPPORT_REQUEST, data),
    {
      onSuccess: (data) => {
        if (options?.onSuccess) options.onSuccess(data);
      },
      onError: (error) => {
        if (options?.onError) options.onError(error);
      },
    }
  );
};

/**
 * In-app AI Help Assistant (SPEC-v1 §7). POSTs a grounded how-to question to
 * `POST /api/v2/help-assistant/` (DRF Token auth via the shared axios instance).
 * Payload: { question, images?: string[] (base64 data URLs, max 4), history?:
 * { role: "user"|"assistant", content: string }[] }.
 * Response: { answer: string, sources: { title, category }[] }.
 * `isLoading` (react-query v3) is the double-submit guard (== TanStack `isPending`).
 * Flag-gated: the entry point only mounts when REACT_APP_AI_HELP_ASSISTANT is on;
 * the backend still 404s until AI_HELP_ASSISTANT_ENABLED is also ON.
 */
export const useHelpAssistant = () => {
  return useMutation((data) => API.post(API_PATH.HELP_ASSISTANT, data));
};

// DL-053 — persist per-category In-app notification toggles.
// PATCH /api/v1/notification/preferences/ with only the editable booleans;
// invalidates the cached preferences on success so reads re-sync.
export const useUpdateNotificationPreferences = (options = {}) => {
  const queryClient = useQueryClient();
  return useMutation(
    (data) => API.patch(API_PATH.NOTIFICATION_PREFERENCES, data),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries([QUERY_KEYS.notificationPreferences]);
        if (options?.onSuccess) options.onSuccess(data);
      },
      onError: (error) => {
        if (options?.onError) options.onError(error);
      },
    }
  );
};

/* --------------------------------------------------------------------------
 * Wheel Comments (v1). Mirrors useFavoriteWheel + FavoriteHeartIcon cache
 * invalidation: invalidate the resource query on write, never a manual counter.
 * ------------------------------------------------------------------------ */
export const usePostComment = (wheelId) => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ body, parent = null }) =>
      API.post(
        API_PATH.WHEEL_COMMENTS(wheelId),
        parent ? { body, parent } : { body }
      ),
    {
      onSuccess: (_res, variables) => {
        queryClient.invalidateQueries(["wheel-comments", wheelId]);
        if (variables?.parent) {
          queryClient.invalidateQueries(["comment-replies", variables.parent]);
        }
      },
    }
  );
};

// Author-ONLY (server re-enforces via 403) — mirrors useDeleteComment's cache
// invalidation shape. `variables.parentId` is present only when editing a
// reply, so its comment-replies cache gets refreshed too (the wheel-comments
// cache alone doesn't cover a reply's own paginated query key).
export const useEditComment = (wheelId) => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ commentId, body }) => API.patch(API_PATH.EDIT_COMMENT(commentId), { body }),
    {
      onSuccess: (_res, variables) => {
        queryClient.invalidateQueries(["wheel-comments", wheelId]);
        if (variables?.parentId) {
          queryClient.invalidateQueries(["comment-replies", variables.parentId]);
        }
      },
    }
  );
};

export const useDeleteComment = (wheelId) => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ commentId }) => API.delete(API_PATH.DELETE_COMMENT(commentId)),
    {
      onSuccess: (_res, variables) => {
        queryClient.invalidateQueries(["wheel-comments", wheelId]);
        if (variables?.parentId) {
          queryClient.invalidateQueries(["comment-replies", variables.parentId]);
        }
      },
    }
  );
};

export const useReportComment = (wheelId) => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ commentId, reason }) =>
      API.post(API_PATH.REPORT_COMMENT(commentId), reason ? { reason } : {}),
    {
      onSuccess: (_res, variables) => {
        queryClient.invalidateQueries(["wheel-comments", wheelId]);
        if (variables?.parentId) {
          queryClient.invalidateQueries(["comment-replies", variables.parentId]);
        }
      },
    }
  );
};

export const useLikeComment = (wheelId) => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ commentId }) => API.post(API_PATH.LIKE_COMMENT(commentId)),
    {
      onSuccess: (_res, variables) => {
        queryClient.invalidateQueries(["wheel-comments", wheelId]);
        if (variables?.parentId) {
          queryClient.invalidateQueries(["comment-replies", variables.parentId]);
        }
      },
    }
  );
};

export const useSetWheelCommentsEnabled = (wheelId) => {
  return useMutation(({ comments_enabled }) =>
    API.post(API_PATH.WHEEL_COMMENTS_SETTINGS(wheelId), { comments_enabled })
  );
};
